<template>
  <div class="cycle-situation-wrapper">
    <div v-if="isLoading" class="text-left">
      <b-spinner label="Carregando..."></b-spinner>
      <span class="ml-2">Carregando informações dos ciclos...</span>
    </div>

    <div v-else class="cycle-situations">
      <b-tabs content-class="mt-3" fill v-if="studentCycles.length > 0">
        <b-tab
          v-for="(cycleData, index) in studentCycles"
          :key="`cycle-${cycleData.cycle.id}`"
          :title="`${cycleData.cycle.name}`"
          title-item-class="cycle-tab-title"
        >
          <template #title>
            <font-awesome-icon
              v-if="
                [
                  'retained',
                  'cycle_retention',
                  'conditional_progression',
                ].includes(cycleData.situation) && index < 2
              "
              icon="fas fa-triangle-exclamation"
              style="color: rgb(191, 24, 24)"
              size="lg"
              class="mr-2"
            />
            {{ cycleData.cycle.name }}
          </template>

          <!-- {{ cycleData.situation }} -->
          <div class="cycle-form text-left">
            <b-form @submit.prevent="submitAllCycleSituations">
              <b-form-group>
                <label for="began_at">Início do Ciclo na Escola da Serra</label>
                <b-form-input
                  v-model="cycleData.began_at"
                  type="date"
                  id="began_at"
                  :disabled="readOnly"
                ></b-form-input>
              </b-form-group>

              <b-form-group>
                <label for="target_date">Expectativa de Conclusão</label>
                <b-form-input
                  v-model="cycleData.target_date"
                  type="date"
                  id="target_date"
                  :disabled="readOnly"
                ></b-form-input>
              </b-form-group>

              <b-form-group>
                <label for="finished_at">Data de Conclusão Real</label>
                <b-form-input
                  v-model="cycleData.finished_at"
                  type="date"
                  id="finished_at"
                  @change="onChangeFinishDate($event, index)"
                  :disabled="readOnly"
                ></b-form-input>
              </b-form-group>

              <b-form-group label="Situação no Ciclo">
                <b-form-select
                  v-model="cycleData.situation"
                  :options="situationOptions"
                  @change="onSituationChange($event, index)"
                  :disabled="readOnly"
                ></b-form-select>
              </b-form-group>

              <b-form-group
                v-if="cycleData.situation === 'retained'"
                label="Anos de Retenção"
                style="margin-top: 15px"
              >
                <div class="retention-years-checkboxes">
                  <b-form-checkbox
                    v-model="cycleData.retained_year_1"
                    :disabled="readOnly"
                    @change="onRetentionYearChange(index)"
                  >
                    1º ano
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="cycleData.retained_year_2"
                    :disabled="readOnly"
                    @change="onRetentionYearChange(index)"
                  >
                    2º ano
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-if="isEMCycle(cycleData.cycle.id)"
                    v-model="cycleData.retained_year_3"
                    :disabled="readOnly"
                    @change="onRetentionYearChange(index)"
                  >
                    3º ano
                  </b-form-checkbox>
                </div>
              </b-form-group>

              <b-form-group
                v-if="cycleData.situation === 'conditional_progression'"
                label="Áreas do Conhecimento"
                style="margin-top: 15px"
              >
                <multiselect
                  v-model="cycleData.subjectsPendencies"
                  :options="availableSubjectsByCycle[cycleData.cycle.id] || []"
                  :multiple="true"
                  placeholder="Selecione as áreas do conhecimento"
                  label="designation"
                  track-by="subject_id"
                  selectLabel=""
                  :disabled="readOnly"
                >
                  <span slot="noResult">Nada encontrado</span>
                </multiselect>
              </b-form-group>

              <div class="mt-3" v-if="!readOnly">
                <b-button type="submit" variant="primary"
                  >Salvar Todos os Ciclos</b-button
                >
              </div>
            </b-form>
          </div>
        </b-tab>
      </b-tabs>

      <div v-if="studentCycles.length === 0" class="text-left">
        <p>Nenhum ciclo encontrado para este aluno.</p>
      </div>
    </div>
  </div>
</template>

<script>
import httpRda from "../http-rda";
import moment from "moment";
import {library} from "@fortawesome/fontawesome-svg-core";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";

library.add(faTriangleExclamation);

export default {
  name: "StudentCycleSituation",
  props: {
    studentId: {
      type: Number,
      required: true,
    },
    cycleId: {
      type: Number,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    FontAwesomeIcon,
  },
  data() {
    return {
      FUNDAMENTAL_CYCLES: [1, 2, 3, 10],
      EM_CYCLES: [4, 5],
      studentCycles: [],
      previousStudentCycles: [],
      availableSubjectsByCycle: {},
      isLoading: false,
      situationOptions: [
        {value: null, text: "Selecione uma opção", disabled: true},
        {value: "regular", text: "Regular"},
        {value: "retained", text: "Retido"},
        {
          value: "cycle_retention",
          text: "Permaneceu no ciclo",
        },
        {value: "conditional_progression", text: "Com pendências"},
        {value: "approved", text: "Aprovado"},
      ],
    };
  },
  methods: {
    isEMCycle(cycleId) {
      return this.EM_CYCLES.includes(cycleId);
    },
    onSituationChange(value, index) {
      if (this.readOnly) return;

      const previousSituation = this.previousStudentCycles[index]
        ? this.previousStudentCycles[index].situation
        : null;

      // Não ajusta mais automaticamente a data ao selecionar 'retained'
      // O ajuste será feito pelos checkboxes
      if (
        this.studentCycles[index].target_date &&
        previousSituation === "retained" &&
        value !== "retained"
      ) {
        // Ao mudar de 'retained' para outra situação, remove os anos adicionados
        const previousRetainedYears = this.previousStudentCycles[index]
          ? (this.previousStudentCycles[index].retained_year_1 ? 1 : 0) +
            (this.previousStudentCycles[index].retained_year_2 ? 1 : 0) +
            (this.previousStudentCycles[index].retained_year_3 ? 1 : 0)
          : 0;

        if (previousRetainedYears > 0) {
          const targetDate = moment(this.studentCycles[index].target_date);
          this.studentCycles[index].target_date = targetDate
            .subtract(previousRetainedYears, "year")
            .format("YYYY-MM-DD");
        }
      }

      // Limpar checkboxes de retenção quando mudar para outra situação
      if (value !== "retained") {
        this.studentCycles[index].retained_year_1 = false;
        this.studentCycles[index].retained_year_2 = false;
        this.studentCycles[index].retained_year_3 = false;
      }

      if (value === "approved") {
        this.studentCycles[index].finished_at = moment().format("YYYY-MM-DD");
      } else if (value === "conditional_progression") {
        this.studentCycles[index].finished_at = null;
      } else {
        this.studentCycles[index].finished_at = null;
      }
      this.previousStudentCycles = JSON.parse(
        JSON.stringify(this.studentCycles),
      );
    },
    onRetentionYearChange(index) {
      if (this.readOnly) return;
      if (!this.studentCycles[index].target_date) return;

      // Calcula quantos anos estavam marcados anteriormente
      const previousRetainedYears = this.previousStudentCycles[index]
        ? (this.previousStudentCycles[index].retained_year_1 ? 1 : 0) +
          (this.previousStudentCycles[index].retained_year_2 ? 1 : 0) +
          (this.previousStudentCycles[index].retained_year_3 ? 1 : 0)
        : 0;

      // Calcula quantos anos estão marcados agora
      const currentRetainedYears =
        (this.studentCycles[index].retained_year_1 ? 1 : 0) +
        (this.studentCycles[index].retained_year_2 ? 1 : 0) +
        (this.studentCycles[index].retained_year_3 ? 1 : 0);

      // Calcula a diferença
      const yearsDiff = currentRetainedYears - previousRetainedYears;

      if (yearsDiff !== 0) {
        const targetDate = moment(this.studentCycles[index].target_date);
        this.studentCycles[index].target_date = targetDate
          .add(yearsDiff, "year")
          .format("YYYY-MM-DD");
      }

      this.previousStudentCycles = JSON.parse(
        JSON.stringify(this.studentCycles),
      );
    },
    onChangeFinishDate(value, index) {
      if (this.readOnly) return;

      if (moment(value).isValid()) {
        this.studentCycles[index].situation = "approved";
      } else {
        this.studentCycles[index].situation = null;
      }
    },
    async getStudentCyclesAndSubjects() {
      if (!this.studentId) {
        return;
      }
      this.isLoading = true;

      try {
        const responseSubjects = await httpRda.get(
          `/cycles/subjects?student_id=${this.studentId}`,
        );
        this.availableSubjectsByCycle =
          responseSubjects.data && responseSubjects.data.result
            ? responseSubjects.data.result
            : null;
      } catch (err) {
        this.availableSubjectsByCycle = err;
      }

      try {
        const responseCycles = await httpRda.get(
          `/cycle-situations/${this.studentId}`,
        );
        this.studentCycles =
          responseCycles.data && responseCycles.data.result
            ? responseCycles.data.result
            : null;

        if (this.studentCycles && this.studentCycles.length > 0) {
          this.studentCycles.forEach((cycleData) => {
            if (!cycleData.target_date) {
              cycleData.target_date = null;
            }

            if (cycleData.retained_year_1 === undefined) {
              cycleData.retained_year_1 = false;
            }
            if (cycleData.retained_year_2 === undefined) {
              cycleData.retained_year_2 = false;
            }
            if (cycleData.retained_year_3 === undefined) {
              cycleData.retained_year_3 = false;
            }
          });
        }
        this.previousStudentCycles = JSON.parse(
          JSON.stringify(this.studentCycles),
        );
        this.isLoading = false;
      } catch (err) {
        this.studentCycles = err;
        this.isLoading = false;
      }

      this.isLoading = false;
    },

    async submitAllCycleSituations() {
      if (this.readOnly) return;

      const validCycles = this.studentCycles.filter((c) => c.situation);
      const someCycleIncomplete = this.studentCycles.some(
        (c) => (c.target_date || c.began_at || c.finished_at) && !c.situation,
      );

      if (someCycleIncomplete) {
        this.$bvToast.toast(
          "Preencha a situação de todos os ciclos antes de salvar",
          {
            title: "Atenção",
            variant: "warning",
            solid: true,
          },
        );
        return;
      }

      if (validCycles.length === 0) {
        this.$bvToast.toast("Preencha ao menos um ciclo para salvar", {
          title: "Atenção",
          variant: "warning",
          solid: true,
        });
        return;
      }

      const cyclesPayload = validCycles
        .filter((cycleData) => cycleData.target_date)
        .map((cycleData) => ({
          cycleId: cycleData.cycle.id,
          situation: cycleData.situation,
          target_date: cycleData.target_date,
          began_at: cycleData.began_at,
          finished_at: cycleData.finished_at,
          retained_year_1:
            cycleData.situation === "retained"
              ? cycleData.retained_year_1
              : false,
          retained_year_2:
            cycleData.situation === "retained"
              ? cycleData.retained_year_2
              : false,
          retained_year_3:
            cycleData.situation === "retained"
              ? cycleData.retained_year_3
              : false,
          subjects:
            cycleData.situation === "conditional_progression"
              ? (cycleData.subjectsPendencies || []).map((s) => ({
                  id: s.subject_id,
                  version: s.version,
                }))
              : [],
        }));

      try {
        await httpRda.post(`/cycle-situations/${this.studentId}`, {
          cycles: cyclesPayload,
        });
        this.$bvToast.toast("Situações dos ciclos salvas com sucesso!", {
          title: "Sucesso",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        this.$bvToast.toast("Erro ao salvar as situações dos ciclos", {
          title: "Erro",
          variant: "danger",
          solid: true,
        });
      }
    },
  },
  mounted() {
    this.getStudentCyclesAndSubjects();
  },
};
</script>

<style lang="scss" scoped>
.cycle-situation-wrapper {
  width: 100%;
  margin-bottom: 20px;
}

.cycle-situations {
  width: 100%;
}

.cycle-form {
  max-width: 600px;
  padding: 15px 0;

  h5 {
    margin-bottom: 15px;
    font-weight: 600;
  }
}

/* Estilos para as abas */
::v-deep .nav-tabs {
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
  border-radius: 4px 4px 0 0;
}

::v-deep .nav-tabs .nav-link {
  border: none;
  color: #495057;
  font-weight: 500;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease-in-out;
  position: relative;
}

::v-deep .nav-tabs .nav-link:hover {
  color: #212529;
  background-color: rgba(0, 0, 0, 0.05);
}

::v-deep .nav-tabs .nav-link.active {
  color: #b23066;
  background-color: #fff;
  border-bottom: 2px solid #b23066;
}

::v-deep .tab-content {
  padding: 20px;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 4px 4px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Estilos para alinhar o conteúdo à esquerda */
.text-left {
  text-align: left;
}

/* Estilos adicionais para o multiselect */
::v-deep .multiselect {
  min-height: 38px;
}

::v-deep .multiselect__tags {
  min-height: 38px;
  padding: 6px 40px 0 8px;
  border-radius: 4px;
  border: 1px solid #ced4da;
}

::v-deep .multiselect__select {
  height: 38px;
}

::v-deep .multiselect__placeholder {
  padding-top: 0;
  margin-bottom: 0;
}

::v-deep .multiselect__content-wrapper {
  border: 1px solid #ced4da;
  border-top: none;
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
}

::v-deep input[type="date"] {
  width: 100%;
  margin-bottom: 15px;
}

::v-deep .multiselect--disabled {
  opacity: 0.6;
  pointer-events: none;
}

.retention-years-checkboxes {
  display: flex;
  gap: 20px;
  align-items: center;
}
</style>
